import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { jwtDecode } from "jwt-decode";
import { buildIdmUrl } from "./services/idmConfig";

// ==== Configuration (adjust as needed) ====
export const TOKEN_ENDPOINT = buildIdmUrl("/realms/platform/protocol/openid-connect/token");
const AUTHORIZATION_ENDPOINT = buildIdmUrl("/realms/platform/protocol/openid-connect/auth");
export const CLIENT_ID = "api";
export const ACCESS_KEY = "access_token";
export const REFRESH_KEY = "refresh_token";

// Recommended: restrict scopes to what you actually need
const SCOPE = "openid profile email";

// SPA redirect URI (MUST be whitelisted in Keycloak 'Valid Redirect URIs' and 'Web origins')
const REDIRECT_URI = window.location.origin + "/";

// Storage helpers — sessionStorage by default to avoid lingering tokens after tab closes.
const storage = sessionStorage;

// Utility: base64url encoding
const base64url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");

const REQUIRED_ROLE = "admin";
// Utility: random string for PKCE verifier/state
const randomString = (length = 64) => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => ("0" + (b & 0xff).toString(16)).slice(-2)).join("");
};

// Build a SHA-256 code challenge from a verifier
const pkceChallengeFromVerifier = async (verifier) => {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64url(digest);
};

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(() => storage.getItem(ACCESS_KEY));
  const [refreshToken, setRefreshToken] = useState(() => storage.getItem(REFRESH_KEY));
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // store absolute expiry time in ms for proactive refresh
  const accessExpiryRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const refreshPromiseRef = useRef(null);
  const refreshTokensRef = useRef(null);

  // StrictMode guards (protect against double effect & duplicate login redirects)
  const handledCallbackRef = useRef(false);
  const loginKickoffRef = useRef(false);

  // ---- Token helpers ----
  const tokenHasAdminRole = useCallback((token) => {
    try {
      const payload = jwtDecode(token);
      const realmRoles = payload?.realm_access?.roles || [];
      const clientRoles = payload?.resource_access?.[CLIENT_ID]?.roles || [];
      return [...realmRoles, ...clientRoles].includes(REQUIRED_ROLE);
    } catch (err) {
      console.error("Failed to decode access token", err);
      return false;
    }
  }, []);

  const clearTokens = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    accessExpiryRef.current = null;
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    storage.removeItem(ACCESS_KEY);
    storage.removeItem(REFRESH_KEY);
  }, []);

  const scheduleRefresh = useCallback((expiresInSec) => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    // Refresh before expiry with slight jitter to avoid thundering herd
    const jitterSec = Math.floor(Math.random() * 15); // 0-14s
    const refreshInMs = Math.max(1000, (expiresInSec - 40 - jitterSec) * 1000);
    const runRefresh = async () => {
      if (typeof refreshTokensRef.current === "function") {
        return refreshTokensRef.current();
      }
      throw new Error("Refresh function missing");
    };

    refreshTimerRef.current = window.setTimeout(async () => {
      try {
        await runRefresh();
      } catch (e) {
        console.error("Auto-refresh failed", e);
        // Retry once after 10s without hard reload
        setTimeout(() => {
          runRefresh().catch((err) => console.error("Auto-refresh retry failed", err));
        }, 10_000);
      }
    }, refreshInMs);
  }, []); // safe: uses refreshTokens via closure at call time

  const persistTokens = useCallback(
    (t) => {
      if (!tokenHasAdminRole(t.access_token)) {
        clearTokens();
        const message = "User is missing the required admin role.";
        setAuthError(message);
        throw new Error(message);
      }

      setAccessToken(t.access_token);
      setRefreshToken(t.refresh_token);
      storage.setItem(ACCESS_KEY, t.access_token);
      storage.setItem(REFRESH_KEY, t.refresh_token);

      const now = Date.now();
      const accessExpiry = now + t.expires_in * 1000;
      accessExpiryRef.current = accessExpiry;
      scheduleRefresh(t.expires_in);
      setAuthError(null);
    },
    [clearTokens, scheduleRefresh, tokenHasAdminRole]
  );

  const exchangeCodeForTokens = useCallback(
    async (code, verifier, redirectUri) => {
      const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: CLIENT_ID,
        code,
        redirect_uri: redirectUri,
        code_verifier: verifier,
      });

      const res = await fetch(TOKEN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Token exchange failed${text ? `: ${text}` : ""}`);
      }
      const json = await res.json();

      persistTokens(json);

      // one-shot PKCE artifacts
      sessionStorage.removeItem("pkce_state");
      sessionStorage.removeItem("pkce_verifier");
      sessionStorage.removeItem("pkce_redirect_uri");
    },
    [persistTokens]
  );

  const refreshTokens = useCallback(async () => {
    if (!refreshToken) throw new Error("No refresh token");

    // Serialize refresh calls to avoid concurrent use of a rotating refresh token
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const run = (async () => {
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        client_id: CLIENT_ID,
        refresh_token: refreshToken,
      });

      const res = await fetch(TOKEN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Refresh failed${text ? `: ${text}` : ""}`);
      }

      const json = await res.json();
      persistTokens(json);
      return json.access_token;
    })();

    refreshPromiseRef.current = run;
    try {
      return await run;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [persistTokens, refreshToken]);
  useEffect(() => {
    refreshTokensRef.current = refreshTokens;
  }, [refreshTokens]);

  const ensureFreshToken = useCallback(async () => {
    if (!accessToken) return null;
    // if within 25s of expiry, proactively refresh
    const now = Date.now();
    if (!accessExpiryRef.current || accessExpiryRef.current - now < 25_000) {
      try {
        return await refreshTokens();
      } catch (e) {
        console.warn("ensureFreshToken refresh failed", e);
        return null;
      }
    }
    return accessToken;
  }, [accessToken, refreshTokens]);

  const login = useCallback(async () => {
    // Reset any previous error when user explicitly retries
    setAuthError(null);

    const verifier = randomString(64);
    const challenge = await pkceChallengeFromVerifier(verifier);
    const state = randomString(24);

    sessionStorage.setItem("pkce_verifier", verifier);
    sessionStorage.setItem("pkce_state", state);
    sessionStorage.setItem("pkce_redirect_uri", REDIRECT_URI);

    const authUrl = new URL(AUTHORIZATION_ENDPOINT);
    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("client_id", CLIENT_ID);
    authUrl.searchParams.set("redirect_uri", REDIRECT_URI);
    authUrl.searchParams.set("scope", SCOPE);
    authUrl.searchParams.set("code_challenge_method", "S256");
    authUrl.searchParams.set("code_challenge", challenge);
    authUrl.searchParams.set("state", state);

    window.location.assign(authUrl.toString());
  }, []);

  const logout = useCallback(() => {
    clearTokens();

    // Optional: end-session endpoint call here if desired.

    // Force a clean reload to trigger login flow again
    window.location.replace(window.location.origin + window.location.pathname);
  }, [clearTokens]);

  const getAccessToken = useCallback(async () => {
    if (!accessToken && refreshToken) {
      try {
        const t = await refreshTokens();
        return t;
      } catch {
        return null;
      }
    }
    return ensureFreshToken();
  }, [accessToken, ensureFreshToken, refreshToken, refreshTokens]);

  const fetchWithAuth = useCallback(
    async (input, init = {}) => {
      let token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");

      const res = await fetch(input, {
        ...init,
        headers: {
          ...(init.headers || {}),
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        // Try refreshing once
        token = await refreshTokens();
        const retry = await fetch(input, {
          ...init,
          headers: {
            ...(init.headers || {}),
            Authorization: `Bearer ${token}`,
          },
        });
        return retry;
      }

      return res;
    },
    [getAccessToken, refreshTokens]
  );

  // Parse URL for auth code on first load (guarded against double-run)
  useEffect(() => {
    if (handledCallbackRef.current) return;
    handledCallbackRef.current = true;

    (async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        const state = url.searchParams.get("state");
        const storedState = sessionStorage.getItem("pkce_state");
        const verifier = sessionStorage.getItem("pkce_verifier");
        const usedRedirect = sessionStorage.getItem("pkce_redirect_uri") || REDIRECT_URI;

        if (code) {
          if (!state || !storedState || state !== storedState) {
            throw new Error("Invalid OAuth state");
          }
          if (!verifier) throw new Error("Missing PKCE verifier");

          await exchangeCodeForTokens(code, verifier, usedRedirect);

          // Clean up URL params but keep origin + path + hash (for SPA routers)
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          const cleaned = url.origin + url.pathname + url.hash; // keep SPA hash if any
          window.history.replaceState({}, "", cleaned || "/");
        } else if (refreshToken) {
          await ensureFreshToken();
        }
      } catch (err) {
        console.error(err);
        setAuthError(err.message || "Auth error");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [ensureFreshToken, exchangeCodeForTokens, refreshToken]);

  const value = useMemo(
    () => ({
      isAuthenticated: Boolean(accessToken),
      isLoading,
      accessToken,
      authError,
      login,
      logout,
      getAccessToken,
      fetchWithAuth,
    }),
    [accessToken, fetchWithAuth, getAccessToken, isLoading, login, logout, authError]
  );

  // Gate: if unauthenticated and not loading, kick off login (guarded)
  useEffect(() => {
    if (!isLoading && !accessToken && !refreshToken && !authError) {
      if (loginKickoffRef.current) return;
      loginKickoffRef.current = true;
      // Defer to next tick to avoid any race with initial effect
      queueMicrotask(() => login());
    }
  }, [accessToken, isLoading, login, refreshToken, authError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// Component that prevents rendering children until authenticated
export const RequireAuth = ({ fallback = null, children }) => {
  const { isAuthenticated, isLoading, authError, login } = useAuth();

  if (isLoading) return <>{fallback ?? <div className="p-6 text-center">Loading…</div>}</>;
  if (authError)
    return (
      <div className="p-6 text-center">
        Auth error: {authError}{" "}
        <button className="px-3 py-1 border rounded" onClick={login}>
          Retry
        </button>
      </div>
    );
  if (!isAuthenticated) return <>{fallback}</>;
  return <>{children}</>;
};
